/**
 * Distillation — the graduation mechanism.
 *
 * Input: a recorded *trajectory* (the ordered primitive calls the strong
 * model made to accomplish a novel task) plus the task-level parameters
 * (owner/repo, known server-side).
 *
 * Output: a single parameterized *domain* macro that replays the same
 * workflow for new parameters, with NO model call at runtime.
 *
 * The structural generalization is done by **value-tracing**: for each
 * argument of each recorded step we ask "where did this value come from?"
 *   - it equals a task parameter        -> bind to that parameter
 *   - it appears in an earlier step's    -> bind to steps[j].<json-path>
 *     output
 *   - otherwise                          -> keep it as a literal
 *
 * This is a genuine, general distillation (not a hardcoded template): it
 * turns a concrete observed run into a parameterized program. The handler
 * is a deterministic interpreter that re-runs the recorded primitive
 * sequence — it NEVER evals generated code, so a distilled macro is exactly
 * as safe as the primitives it composes.
 */
import { defineMacro, testMacro, type AuthoredMacro } from "@macrokit/authoring";
import type { MacroContext, MacroRegistry } from "@macrokit/runtime";
import { z } from "zod";
import { WRITE_PRIMITIVES } from "./primitives.js";

export interface TrajectoryStep {
  tool: string;
  args: Record<string, unknown>;
  result: unknown;
}

export type Binding =
  | { from: "param"; name: string }
  | { from: "step"; index: number; path: Array<string | number> }
  | { from: "literal"; value: unknown };

export interface PlanStep {
  tool: string;
  bindings: Record<string, Binding>;
}

/**
 * How to extract a parameter's value from a given run — used by cluster
 * replay-verification. Single-trajectory plans leave this undefined.
 */
export type ParamSource =
  | { kind: "taskParam"; key: string }
  | { kind: "argValue"; stepIndex: number; key: string };

export interface PlanParam {
  name: string;
  type: "string" | "number";
  /** Present for cluster-distilled params; absent for single-trajectory plans. */
  source?: ParamSource;
}

export interface DistilledPlan {
  name: string;
  intent: string;
  params: PlanParam[];
  steps: PlanStep[];
  trust: "auto" | "candidate";
  /** Stable signature = tool sequence; used to dedupe equivalent workflows. */
  signature: string;
  /** Where the name/intent came from — for honest UI labeling. */
  proposedBy: "claude" | "heuristic";
}

// ---------------------------------------------------------------------------
// Value-tracing
// ---------------------------------------------------------------------------

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a && b && typeof a === "object") {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

/** Walk a JSON value yielding [path, leafOrNode] pairs (depth-first). */
function* walk(
  value: unknown,
  path: Array<string | number> = [],
): Generator<[Array<string | number>, unknown]> {
  yield [path, value];
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) yield* walk(value[i], [...path, i]);
  } else if (value && typeof value === "object") {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      yield* walk(v, [...path, k]);
    }
  }
}

export function getByPath(root: unknown, path: Array<string | number>): unknown {
  let cur: unknown = root;
  for (const seg of path) {
    if (cur == null) return undefined;
    cur = (cur as Record<string | number, unknown>)[seg];
  }
  return cur;
}

function traceArg(
  value: unknown,
  taskParams: Record<string, unknown>,
  priorResults: unknown[],
): Binding {
  // 1. Task parameter?
  for (const [name, pv] of Object.entries(taskParams)) {
    if (deepEqual(value, pv)) return { from: "param", name };
  }
  // 2. Earlier step output? Prefer the earliest step + shortest path.
  for (let index = 0; index < priorResults.length; index++) {
    for (const [path, node] of walk(priorResults[index])) {
      if (path.length > 0 && deepEqual(value, node)) {
        return { from: "step", index, path };
      }
    }
  }
  // 3. Literal.
  return { from: "literal", value };
}

export function valueTrace(
  taskParams: Record<string, unknown>,
  trajectory: TrajectoryStep[],
): { steps: PlanStep[]; paramsUsed: Set<string> } {
  const steps: PlanStep[] = [];
  const paramsUsed = new Set<string>();
  const priorResults: unknown[] = [];

  for (const step of trajectory) {
    const bindings: Record<string, Binding> = {};
    for (const [key, value] of Object.entries(step.args)) {
      const b = traceArg(value, taskParams, priorResults);
      bindings[key] = b;
      if (b.from === "param") paramsUsed.add(b.name);
    }
    steps.push({ tool: step.tool, bindings });
    priorResults.push(step.result);
  }
  return { steps, paramsUsed };
}

// ---------------------------------------------------------------------------
// Plan assembly
// ---------------------------------------------------------------------------

export function signatureOf(trajectory: TrajectoryStep[]): string {
  return trajectory.map((s) => s.tool).join(">");
}

/**
 * Build the full DistilledPlan from a trajectory. Name/intent/trust may be
 * supplied by a strong model (proposal); when absent we derive them
 * heuristically so the loop also runs offline (smoke tests).
 */
export function buildPlan(
  taskParams: Record<string, unknown>,
  trajectory: TrajectoryStep[],
  proposal?: { name?: string; intent?: string; proposedBy?: "claude" | "heuristic" },
): DistilledPlan {
  const { steps, paramsUsed } = valueTrace(taskParams, trajectory);
  const params = [...paramsUsed].map((name) => ({
    name,
    type: typeof taskParams[name] === "number" ? ("number" as const) : ("string" as const),
  }));

  const touchesWrite = trajectory.some((s) => WRITE_PRIMITIVES.has(s.tool));
  const signature = signatureOf(trajectory);

  const name = sanitizeName(proposal?.name) ?? heuristicName(trajectory);
  const intent =
    proposal?.intent?.trim() || heuristicIntent(trajectory);

  return {
    name,
    intent,
    params,
    steps,
    trust: touchesWrite ? "candidate" : "auto",
    signature,
    proposedBy: proposal?.proposedBy ?? "heuristic",
  };
}

function sanitizeName(raw?: string): string | undefined {
  if (!raw) return undefined;
  const s = raw
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "m_$1");
  return /^[a-z][a-z0-9_]*$/.test(s) ? s : undefined;
}

function heuristicName(trajectory: TrajectoryStep[]): string {
  const tools = trajectory.map((s) => s.tool);
  if (tools.includes("gh_suggest_labels_dryrun") && tools.includes("gh_list_pulls")) {
    return "triage_newest_pull";
  }
  if (tools.includes("gh_list_issues")) return "summarize_open_issues";
  if (tools.includes("gh_list_pulls")) return "review_pulls";
  return "github_workflow";
}

function heuristicIntent(trajectory: TrajectoryStep[]): string {
  const tools = new Set(trajectory.map((s) => s.tool));
  if (tools.has("gh_suggest_labels_dryrun") && tools.has("gh_list_pulls")) {
    return (
      "Triage the newest open pull request in a repository: list open PRs, " +
      "inspect the newest one and its changed files, then propose triage " +
      "labels (dry-run)."
    );
  }
  if (tools.has("gh_list_issues")) {
    return "Summarize the open issues in a repository: list issues and report counts, labels, and notable items.";
  }
  return "Run a recorded GitHub maintainer workflow over a repository.";
}

// ---------------------------------------------------------------------------
// Plan -> macro (the interpreter)
// ---------------------------------------------------------------------------

function resolveBinding(b: Binding, args: Record<string, unknown>, stepResults: unknown[]): unknown {
  switch (b.from) {
    case "param":
      return args[b.name];
    case "literal":
      return b.value;
    case "step":
      return getByPath(stepResults[b.index], b.path);
  }
}

/**
 * Compile a DistilledPlan into an executable Macrokit macro. The handler is
 * a deterministic interpreter: it resolves each step's argument bindings and
 * invokes the corresponding primitive macro's handler from the primitives
 * registry. No code generation, no eval.
 */
export function buildMacroFromPlan(
  plan: DistilledPlan,
  primitives: MacroRegistry,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): AuthoredMacro<any, any> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const p of plan.params) {
    shape[p.name] = p.type === "number" ? z.number() : z.string();
  }
  const schema = z.object(shape);

  return defineMacro({
    name: plan.name,
    intent: plan.intent,
    category: "domain",
    schema,
    handler: async (args, ctx: MacroContext) => {
      const stepResults: unknown[] = [];
      const trace: Array<{ tool: string; args: Record<string, unknown>; result: unknown }> = [];
      for (const step of plan.steps) {
        const primitive = primitives.lookup(step.tool);
        if (!primitive) {
          throw new Error(`distilled macro references unknown primitive "${step.tool}"`);
        }
        const resolved: Record<string, unknown> = {};
        for (const [key, binding] of Object.entries(step.bindings)) {
          resolved[key] = resolveBinding(binding, args as Record<string, unknown>, stepResults);
        }
        const parsed = primitive.schema.parse(resolved);
        const result = await primitive.handler(parsed, ctx);
        stepResults.push(result);
        trace.push({ tool: step.tool, args: resolved, result });
      }
      return {
        servedByMacro: true,
        macro: plan.name,
        trust: plan.trust,
        steps: trace,
        result: stepResults[stepResults.length - 1] ?? null,
      };
    },
  });
}

// ===========================================================================
// CLUSTER-BASED DISTILLATION (STUDIO_DISTILLATION_DESIGN.md upgrades 1 & 2)
//
// Single-trajectory value-tracing can't tell which literals SHOULD be
// parameters, nor whether the procedure generalizes. Clustering fixes both:
//   - value-trace ACROSS N runs of the same signature: constant -> literal,
//     varies -> parameter (variation reveals the parameters automatically);
//   - replay-verify the resulting macro against all N runs before granting
//     trust:"auto". A macro that can't reproduce its own cluster stays
//     "candidate". This is what makes "auto" honest.
//
// This is purely additive — the single-trajectory path above is unchanged,
// so the pre-authored launch demo is unaffected.
// ===========================================================================

export interface ClusterRun {
  taskParams: Record<string, unknown>;
  trajectory: TrajectoryStep[];
}

function sanitizeParamName(key: string): string {
  const s = key.toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").replace(/^([0-9])/, "p_$1");
  return /^[a-z][a-z0-9_]*$/.test(s) ? s : "param";
}

function inferType(values: unknown[]): "string" | "number" {
  return values.length > 0 && values.every((v) => typeof v === "number") ? "number" : "string";
}

/**
 * Value-trace across a cluster of runs that share a signature. For each
 * (step, argKey):
 *   - if every run derives it from the same earlier step output -> step binding
 *   - else if every run binds it to the same task parameter      -> param
 *   - else if the value is constant across all runs              -> literal
 *   - else (it varies)                                           -> parameter
 */
export function clusterValueTrace(runs: ClusterRun[]): { steps: PlanStep[]; params: PlanParam[] } {
  const first = runs[0];
  if (!first) return { steps: [], params: [] };

  const steps: PlanStep[] = [];
  // name -> {type, source}; dedupes params shared across steps.
  const params = new Map<string, PlanParam>();

  const addParam = (name: string, type: "string" | "number", source: ParamSource): string => {
    const existing = params.get(name);
    if (existing && JSON.stringify(existing.source) !== JSON.stringify(source)) {
      // Name clash with a different source — disambiguate.
      name = `${name}_${(source as { stepIndex?: number }).stepIndex ?? params.size}`;
    }
    if (!params.has(name)) params.set(name, { name, type, source });
    return name;
  };

  for (let i = 0; i < first.trajectory.length; i++) {
    const tool = first.trajectory[i]!.tool;
    const bindings: Record<string, Binding> = {};
    for (const key of Object.keys(first.trajectory[i]!.args)) {
      const perRun = runs.map((r) => {
        const priorResults = r.trajectory.slice(0, i).map((s) => s.result);
        const value = r.trajectory[i]!.args[key];
        return { value, binding: traceArg(value, r.taskParams, priorResults) };
      });

      const allStepSame =
        perRun.every((p) => p.binding.from === "step") &&
        perRun.every(
          (p) => JSON.stringify(p.binding) === JSON.stringify(perRun[0]!.binding),
        );
      if (allStepSame) {
        bindings[key] = perRun[0]!.binding;
        continue;
      }

      const allParamSame =
        perRun.every((p) => p.binding.from === "param") &&
        perRun.every(
          (p) =>
            (p.binding as { name: string }).name === (perRun[0]!.binding as { name: string }).name,
        );
      if (allParamSame) {
        const name = (perRun[0]!.binding as { name: string }).name;
        const finalName = addParam(name, inferType(perRun.map((p) => p.value)), {
          kind: "taskParam",
          key: name,
        });
        bindings[key] = { from: "param", name: finalName };
        continue;
      }

      const values = perRun.map((p) => p.value);
      const constant = values.every((v) => deepEqual(v, values[0]));
      if (constant) {
        bindings[key] = { from: "literal", value: values[0] };
      } else {
        // Varies across the cluster -> it's a parameter, named after the field.
        const finalName = addParam(sanitizeParamName(key), inferType(values), {
          kind: "argValue",
          stepIndex: i,
          key,
        });
        bindings[key] = { from: "param", name: finalName };
      }
    }
    steps.push({ tool, bindings });
  }

  return { steps, params: [...params.values()] };
}

/** Assemble a DistilledPlan from a cluster (trust defaults to candidate). */
export function buildClusterPlan(
  runs: ClusterRun[],
  proposal?: { name?: string; intent?: string; proposedBy?: "claude" | "heuristic" },
): DistilledPlan {
  const { steps, params } = clusterValueTrace(runs);
  const repTrajectory = runs[0]?.trajectory ?? [];
  const touchesWrite = runs.some((r) => r.trajectory.some((s) => WRITE_PRIMITIVES.has(s.tool)));
  return {
    name: sanitizeName(proposal?.name) ?? heuristicName(repTrajectory),
    intent: proposal?.intent?.trim() || heuristicIntent(repTrajectory),
    params,
    steps,
    trust: touchesWrite ? "candidate" : "candidate", // upgraded by replay-verify
    signature: signatureOf(repTrajectory),
    proposedBy: proposal?.proposedBy ?? "heuristic",
  };
}

/** Extract the macro-arg vector for one run from a cluster plan. */
function runArgs(plan: DistilledPlan, run: ClusterRun): Record<string, unknown> {
  const args: Record<string, unknown> = {};
  for (const p of plan.params) {
    if (!p.source) continue;
    if (p.source.kind === "taskParam") args[p.name] = run.taskParams[p.source.key];
    else args[p.name] = run.trajectory[p.source.stepIndex]?.args[p.source.key];
  }
  return args;
}

// Primitive tool -> GitHubClient method + positional param names. Used to
// build a deterministic playback client from a recorded run.
const PLAYBACK_METHODS: Record<string, { tool: string; params: string[] }> = {
  getRepo: { tool: "gh_get_repo", params: ["owner", "repo"] },
  listPulls: { tool: "gh_list_pulls", params: ["owner", "repo", "state"] },
  getPull: { tool: "gh_get_pull", params: ["owner", "repo", "number"] },
  listPullFiles: { tool: "gh_list_pull_files", params: ["owner", "repo", "number"] },
  listIssues: { tool: "gh_list_issues", params: ["owner", "repo", "state", "labels"] },
};

/**
 * A GitHubClient-shaped object that replays a recorded run: each method
 * returns the recorded result for a matching call, and THROWS on a miss. A
 * miss means the macro regenerated a call that differs from the recording —
 * i.e. the parameterization is wrong — so replay fails loudly.
 */
function makePlaybackGithub(run: ClusterRun): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const [method, spec] of Object.entries(PLAYBACK_METHODS)) {
    obj[method] = async (...callArgs: unknown[]): Promise<unknown> => {
      const provided: Record<string, unknown> = {};
      spec.params.forEach((p, idx) => (provided[p] = callArgs[idx]));
      for (const step of run.trajectory) {
        if (step.tool !== spec.tool) continue;
        if (spec.params.every((p) => deepEqual(step.args[p], provided[p]))) return step.result;
      }
      throw new Error(`playback miss: ${method}(${JSON.stringify(provided)})`);
    };
  }
  return obj;
}

export interface ReplayResult {
  passed: boolean;
  perRun: Array<{ passed: boolean; error?: string }>;
}

/**
 * Replay-verify: run the compiled macro against each recorded run (via the
 * SDK authoring test harness) using a playback GitHub client, and check it
 * reproduces that run's recorded step outputs. Passes only if ALL runs
 * reproduce.
 */
export async function replayVerify(
  plan: DistilledPlan,
  runs: ClusterRun[],
  primitives: MacroRegistry,
): Promise<ReplayResult> {
  const macro = buildMacroFromPlan(plan, primitives);
  const perRun: Array<{ passed: boolean; error?: string }> = [];

  for (const run of runs) {
    const expectedStepResults = run.trajectory.map((s) => s.result);
    const results = await testMacro(macro, {
      fixtures: [
        {
          args: runArgs(plan, run),
          tools: { github: makePlaybackGithub(run) },
          // Projection compared by the custom equals below.
          expected: { __stepResults: expectedStepResults } as unknown,
        },
      ],
      equals: (actual: unknown) => {
        const a = actual as { steps?: Array<{ result: unknown }> };
        const produced = (a.steps ?? []).map((s) => s.result);
        return deepEqual(produced, expectedStepResults);
      },
    });
    const r = results[0]!;
    perRun.push({ passed: r.passed, ...(r.error ? { error: r.error } : {}) });
  }

  return { passed: perRun.every((r) => r.passed), perRun };
}

/**
 * Full cluster distillation: build the plan, replay-verify it, and set trust.
 * A macro graduates to "auto" only if it reproduces every run in its cluster
 * AND performs no (dry-run) write; otherwise it stays "candidate" (awaiting
 * one-click human approval, or re-authoring).
 */
export async function distillCluster(
  runs: ClusterRun[],
  primitives: MacroRegistry,
  proposal?: { name?: string; intent?: string; proposedBy?: "claude" | "heuristic" },
): Promise<{ plan: DistilledPlan; replay: ReplayResult }> {
  const plan = buildClusterPlan(runs, proposal);
  const replay = await replayVerify(plan, runs, primitives);
  const touchesWrite = runs.some((r) => r.trajectory.some((s) => WRITE_PRIMITIVES.has(s.tool)));
  plan.trust = replay.passed && !touchesWrite ? "auto" : "candidate";
  return { plan, replay };
}
